<script lang="ts">
  import type { Project, Task, TaskPriority } from '../lib/types';
  import { categoryIcons, statusAccentColors } from '../lib/utils';
  import { showProjectTooltip, hideProjectTooltip } from '../lib/stores/projectTooltip';
  import { openProjectRail } from '../lib/stores/ui';
  import { subscribeToCachedTasks, fetchProjectTasks } from '../lib/stores/projectTasksCache';
  import { getTaskCounts, subscribeToProjectTaskCounts } from '../lib/useProjectTaskCounts';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    project: Project;
    expanded?: boolean;
    onToggleExpand?: () => void;
    draggable?: boolean;
    onDragStart?: (e: DragEvent) => void;
    onDragEnd?: () => void;
    isDragging?: boolean;
  }

  let {
    project,
    expanded = false,
    onToggleExpand,
    draggable = true,
    onDragStart,
    onDragEnd,
    isDragging = false,
  }: Props = $props();

  // Reactive taskCounts - updates when project prop changes or tasks are modified
  // Initialize with default, then $effect sets proper value and subscribes to updates
  let taskCounts = $state({ total: 0, done: 0, todo: 0 });

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: projectTasksCache pub/sub, re-subscribes on project prop change, returns cleanup
  $effect(() => {
    // Set initial counts (re-runs when project changes since project is referenced)
    taskCounts = getTaskCounts(project);
    // Subscribe to real-time updates
    return subscribeToProjectTaskCounts(project, (counts) => {
      taskCounts = counts;
    });
  });

  let hasTasks = $derived(taskCounts.total > 0);

  // No HTML5 drag on phones: long-press would start a half-working drag. The
  // rail's ⋯ (Move to <status> / Schedule…) is the tap path (plans/mobile-v2.md §3.5).
  let canDrag = $derived(draggable && !$viewport.isMobile);

  // Reactive progress style - updates when taskCounts change
  let chipProgressStyle = $derived.by(() => {
    if (taskCounts.total === 0) return '';
    const pct = Math.round((taskCounts.done / taskCounts.total) * 100);
    if (pct === 0) return '';
    return `--progress: ${pct}%;`;
  });

  let chipProgressComplete = $derived(taskCounts.total > 0 && taskCounts.done === taskCounts.total);

  // Task state from shared cache
  let tasks = $state<Task[]>([]);
  let tasksLoading = $state(false);
  let tasksFetched = $state(false);

  // Subscribe to cache for this project (re-subscribe if project.id changes)
  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: subscribeToCachedTasks pub/sub, re-subscribes on project.id change, returns cleanup
  $effect(() => {
    const projectId = project.id;
    const unsubscribe = subscribeToCachedTasks(projectId, (data) => {
      tasks = data.tasks;
      tasksLoading = data.loading;
      tasksFetched = data.fetched;
    });
    return unsubscribe;
  });

  // Separate incomplete and completed tasks
  let incompleteTasks = $derived(
    tasks
      .filter((t) => t.status === 'todo')
      .sort((a, b) => {
        // Sort by priority (high > medium > low > none), then by position
        const priorityOrder: Record<TaskPriority, number> = { high: 0, medium: 1, low: 2, none: 3 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return a.position - b.position;
      }),
  );

  let completedTasks = $derived(
    tasks
      .filter((t) => t.status !== 'todo')
      .sort((a, b) => {
        // Sort by completion time, most recent first
        const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return bTime - aTime;
      }),
  );

  // Priority border colors for checkbox (matches ProjectTooltip)
  const priorityBorderColors: Record<TaskPriority, string> = {
    high: '#ef4444',
    medium: '#fb923c',
    low: '#60a5fa',
    none: '#d1d5db',
  };

  // Fetch tasks when expanded (uses shared cache with deduplication)
  // eslint-disable-next-line momentium/no-unguarded-effect -- conditional data fetch: triggers fetchProjectTasks when chip expands, read-only trigger with no state write to dependencies
  $effect(() => {
    if (expanded && !tasksFetched && !tasksLoading) {
      fetchProjectTasks(project.id);
    }
  });

  function handleChipClick(e: MouseEvent) {
    // If clicking the expand button, don't open rail
    const target = e.target as HTMLElement;
    if (target.closest('.expand-btn')) {
      return;
    }
    hideProjectTooltip(true);
    openProjectRail(project.id);
  }

  function handleExpandClick(e: MouseEvent) {
    e.stopPropagation();
    // Close tooltip when expanding (it would otherwise stay visible)
    hideProjectTooltip(true);
    onToggleExpand?.();
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (e.target === e.currentTarget) {
        hideProjectTooltip(true);
        openProjectRail(project.id);
      }
    }
    // Toggle expand with arrow keys
    if (e.key === 'ArrowRight' && !expanded) {
      e.preventDefault();
      onToggleExpand?.();
    }
    if (e.key === 'ArrowLeft' && expanded) {
      e.preventDefault();
      onToggleExpand?.();
    }
  }
</script>

<div
  class="expandable-chip {statusAccentColors[project.status]}"
  class:expanded
  class:dragging={isDragging}
  class:has-tasks={hasTasks}
  style={chipProgressStyle}
  data-progress-complete={chipProgressComplete || undefined}
  role="button"
  tabindex="0"
  draggable={canDrag && !expanded}
  ondragstart={!expanded ? onDragStart : undefined}
  ondragend={!expanded ? onDragEnd : undefined}
  onclick={handleChipClick}
  onkeydown={handleKeydown}
  onmouseenter={(e) => !expanded && showProjectTooltip(project, e.currentTarget as HTMLElement)}
  onmouseleave={() => hideProjectTooltip()}
  title={expanded
    ? ''
    : `${project.title} (${project.status})${project.multiWeek ? ` — ${project.totalWeeks} weeks` : ''}`}
>
  <!-- Chip header (always visible) - drag handle when expanded. Not `.drag-handle`:
       the touch layer hides those, and this is the whole header. -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="chip-header"
    class:header-drag={expanded && canDrag}
    draggable={expanded && canDrag}
    ondragstart={expanded ? onDragStart : undefined}
    ondragend={expanded ? onDragEnd : undefined}
  >
    <span class="chip-category">{categoryIcons[project.category]}</span>
    {#if project.isPriority}
      <span class="chip-star">★</span>
    {/if}
    <span class="chip-title">{project.title}</span>

    {#if project.multiWeek}
      {#if project.weekIndex && project.totalWeeks}
        <span class="chip-span">{project.weekIndex}/{project.totalWeeks}</span>
      {:else if project.totalWeeks}
        <span class="chip-span">{project.totalWeeks}w</span>
      {/if}
    {/if}

    {#if hasTasks}
      <span class="chip-task-count" class:all-done={taskCounts.done === taskCounts.total}>
        {taskCounts.done}/{taskCounts.total}
      </span>
    {/if}

    <button
      type="button"
      class="expand-btn"
      onclick={handleExpandClick}
      title={expanded ? 'Collapse' : 'Expand'}
    >
      <span class="expand-chevron" class:open={expanded}>▶</span>
    </button>
  </div>

  <!-- Inline tasks (shown when expanded) -->
  {#if expanded}
    <div class="inline-tasks">
      {#if tasksLoading}
        <div class="tasks-loading">Loading tasks...</div>
      {:else if !hasTasks}
        <div class="no-tasks-message">No tasks yet</div>
      {:else}
        <!-- Incomplete tasks -->
        {#each incompleteTasks as task (task.id)}
          <div class="inline-task">
            <span class="task-checkbox" style="border-color: {priorityBorderColors[task.priority]};"
            ></span>
            <span class="task-title">{task.title}</span>
          </div>
        {/each}

        {#if incompleteTasks.length === 0 && completedTasks.length > 0}
          <div class="all-done-message">All tasks completed! 🎉</div>
        {/if}

        <!-- Completed tasks -->
        {#if completedTasks.length > 0}
          <div class="completed-section">
            <div class="completed-header">Completed ({completedTasks.length})</div>
            {#each completedTasks as task (task.id)}
              <div class="inline-task completed">
                <span class="task-checkbox checked" class:wont-do={task.status === 'wont_do'}>
                  {#if task.status === 'wont_do'}
                    <span class="check-mark">✕</span>
                  {:else}
                    <span class="check-mark">✓</span>
                  {/if}
                </span>
                <span class="task-title">{task.title}</span>
              </div>
            {/each}
          </div>
        {/if}
      {/if}
    </div>
  {/if}
</div>

<style>
  .expandable-chip {
    display: flex;
    flex-direction: column;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    cursor: pointer;
    transition: all 0.15s;
    min-width: 0;
    overflow: hidden;
    position: relative;
  }

  .expandable-chip:hover {
    background: #f3f4f6;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .expandable-chip:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .expandable-chip.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .expandable-chip.expanded {
    background: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    cursor: default;
  }

  /* Task progress cross-hatch fill - applied to header for consistent angle */
  .chip-header {
    position: relative;
    overflow: hidden;
  }

  .expandable-chip[style*='--progress'] .chip-header::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background: repeating-linear-gradient(
      -45deg,
      var(--progress-color, rgba(0, 0, 0, 0.06)),
      var(--progress-color, rgba(0, 0, 0, 0.06)) 2px,
      transparent 2px,
      transparent 5px
    );
    clip-path: polygon(0 0, var(--progress, 0%) 0, calc(var(--progress, 0%) + 0.5rem) 100%, 0 100%);
  }

  /* 100% complete: solid fill, no angled edge */
  .expandable-chip[data-progress-complete] .chip-header::after {
    background: var(--progress-color, rgba(0, 0, 0, 0.06));
    clip-path: none;
  }

  /* Thin angled border at progress edge */
  .expandable-chip[style*='--progress'] .chip-header::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    clip-path: polygon(
      var(--progress, 0%) 0,
      calc(var(--progress, 0%) + 1px) 0,
      calc(var(--progress, 0%) + 0.5rem + 1px) 100%,
      calc(var(--progress, 0%) + 0.5rem) 100%
    );
    background: var(--chip-border-color, #e5e7eb);
    opacity: 0.45;
  }

  /* 100% complete: no border line needed */
  .expandable-chip[data-progress-complete] .chip-header::before {
    display: none;
  }

  /* Ensure header content stays above progress fill */
  .chip-header > * {
    position: relative;
    z-index: 2;
  }

  /* Chip header */
  .chip-header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.375rem;
    min-width: 0;
  }

  .expanded .chip-header {
    border-bottom: 1px solid #f3f4f6;
    padding-bottom: 0.3125rem;
  }

  /* Drag handle when expanded - cursor must override parent's cursor: default */
  .chip-header.header-drag {
    cursor: grab !important;
    border-radius: 0.1875rem 0.1875rem 0 0;
    transition: background 0.15s;
  }

  .chip-header.header-drag:hover {
    background: rgba(59, 130, 246, 0.08);
  }

  .chip-header.header-drag:active {
    cursor: grabbing !important;
  }

  .chip-category {
    font-size: 0.5625rem;
    flex-shrink: 0;
    line-height: 1;
  }

  .chip-star {
    color: #facc15;
    font-size: 0.5625rem;
    flex-shrink: 0;
  }

  .chip-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1f2937;
    min-width: 0;
    flex: 1;
  }

  .chip-span {
    flex-shrink: 0;
    font-size: 0.5rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.15);
    padding: 0 0.1875rem;
    border-radius: 0.125rem;
    letter-spacing: -0.02em;
  }

  .chip-task-count {
    flex-shrink: 0;
    font-size: 0.5rem;
    font-weight: 500;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
  }

  .chip-task-count.all-done {
    color: #16a34a;
  }

  .expand-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    padding: 0;
    background: none;
    border: none;
    border-radius: 0.125rem;
    cursor: pointer;
    color: #9ca3af;
    flex-shrink: 0;
    transition: all 0.15s;
  }

  .expand-btn:hover {
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
  }

  .expand-chevron {
    font-size: 0.5rem;
    transition: transform 0.15s ease;
    display: inline-block;
  }

  .expand-chevron.open {
    transform: rotate(90deg);
  }

  /* Inline tasks */
  .inline-tasks {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    padding: 0.375rem;
    background: #fafafa;
    border-top: 1px solid #f3f4f6;
    height: 10rem;
    overflow-y: auto;

    /* Hide scrollbar until hover */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
  }

  .inline-tasks::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }

  .inline-tasks::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
  }

  .inline-tasks:hover {
    scrollbar-width: thin; /* Firefox */
  }

  .inline-tasks:hover::-webkit-scrollbar-thumb {
    background: #d1d5db;
  }

  .inline-tasks:hover::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  .tasks-loading {
    font-size: 0.5625rem;
    color: #9ca3af;
    padding: 0.5rem 0;
    text-align: center;
  }

  .inline-task {
    display: flex;
    align-items: center;
    gap: 0.3125rem;
    padding: 0.125rem 0;
    min-width: 0;
  }

  .inline-task.completed {
    opacity: 0.6;
  }

  .task-checkbox {
    width: 0.75rem;
    height: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid #d1d5db;
    border-radius: 0.1875rem;
    background: white;
    flex-shrink: 0;
  }

  .task-checkbox.checked {
    background: #3b82f6;
    border-color: #3b82f6;
  }

  .task-checkbox.wont-do {
    background: #9ca3af;
    border-color: #9ca3af;
  }

  .check-mark {
    font-size: 0.5rem;
    color: white;
    font-weight: 700;
    line-height: 1;
  }

  .task-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.625rem;
    color: #374151;
    line-height: 1.3;
  }

  .inline-task.completed .task-title {
    text-decoration: line-through;
    color: #9ca3af;
  }

  .all-done-message {
    font-size: 0.5625rem;
    color: #16a34a;
    padding: 0.25rem 0;
    text-align: center;
  }

  .no-tasks-message {
    font-size: 0.5625rem;
    color: #9ca3af;
    padding: 0.5rem 0;
    text-align: center;
    font-style: italic;
  }

  .completed-section {
    margin-top: 0.25rem;
    padding-top: 0.25rem;
    border-top: 1px dashed #e5e7eb;
  }

  .completed-header {
    font-size: 0.5rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.125rem;
  }

  /* Status accent colors (inherited from global styles, but backup here) */
  .expandable-chip.status-planned {
    --chip-border-color: #93c5fd;
  }

  .expandable-chip.status-active {
    --chip-border-color: #fcd34d;
  }

  .expandable-chip.status-almost {
    --chip-border-color: #fdba74;
  }

  .expandable-chip.status-done {
    --chip-border-color: #86efac;
  }
</style>
